<template>
  <div class="view-page-container">
    <div class="udc">
      <!-- ── HEADER ── -->
      <div class="udc-header">
        <div class="udc-top">
          <div class="udc-left">
            <!-- <div class="udc-icon-wrap">
              <v-icon size="28" color="var(--brand)">mdi-image-multiple-outline</v-icon>
            </div> -->
            <div>
              <div class="udc-name">Gallery</div>
              <div class="udc-meta-row" v-if="galleries.length">
                <div class="udc-meta-item">
                  <div class="meta-dot green"></div>
                  <span class="udc-meta-text"
                    >{{ galleries.length }} album(s)</span
                  >
                </div>
                <span class="udc-meta-sep">|</span>
                <div class="udc-meta-item">
                  <div class="meta-dot purple"></div>
                  <span class="udc-meta-text">{{ totalPhotos }} photo(s)</span>
                </div>
              </div>
              <div class="udc-meta-row" v-else>
                <span class="udc-meta-text"
                  >Manage photo albums and share society highlights</span
                >
              </div>
            </div>
          </div>
          <div class="udc-right">
            <div class="search-wrap">
              <v-icon size="16" color="var(--tx3)">mdi-magnify</v-icon>
              <input
                v-model="search"
                class="search-input"
                placeholder="Search albums..."
                @input="onSearch"
              />
              <v-icon
                v-if="search"
                size="14"
                color="var(--tx3)"
                style="cursor: pointer"
                @click="search = ''"
              >
                mdi-close
              </v-icon>
            </div>
            <button
              class="udc-add-btn"
              @click="$router.push('/societyadmin/gallery/add')"
            >
              <v-icon size="16" color="white">mdi-plus</v-icon>
              Add album
            </button>
          </div>
        </div>
      </div>

      <!-- ── BODY ── -->
      <div class="udc-body">
        <!-- Loading -->
        <div v-if="loading" class="state-center">
          <v-progress-circular indeterminate color="var(--brand)" size="32" />
        </div>

        <!-- Empty -->
        <div v-else-if="galleries.length === 0" class="empty-state">
          <div class="empty-icon-wrap">
            <v-icon size="32" color="var(--tx3)"
              >mdi-folder-multiple-image-outline</v-icon
            >
          </div>
          <div class="empty-title">No albums found</div>
          <div class="empty-sub">
            {{
              search
                ? "Try a different search term."
                : "Create your first album to capture and share moments with residents."
            }}
          </div>
          <button
            v-if="!search"
            class="udc-add-btn mt-4"
            @click="$router.push('/societyadmin/gallery/add')"
          >
            <v-icon size="16" color="white">mdi-plus</v-icon>
            Create album
          </button>
        </div>

        <!-- Grid -->
        <div v-else class="gallery-grid">
          <div v-for="item in galleries" :key="item.id" class="album-card">
            <!-- Cover -->
            <div
              class="album-cover"
              @click="$router.push('/societyadmin/gallery/view/' + item.id)"
            >
              <!-- Actual cover image -->
              <img
                v-if="item.picture_url"
                :src="API_BASE_URL + '/uploads/' + item.picture_url"
                :alt="item.title"
                class="cover-img"
                @error="item.picture_url = null"
              />
              <!-- Placeholder when no image -->
              <div v-else class="cover-placeholder">
                <v-icon size="40" color="var(--tx3)">mdi-image-multiple-outline</v-icon>
                <span class="placeholder-label">No cover photo</span>
              </div>

              <div class="cover-overlay">
                <div class="view-btn">
                  <v-icon size="16" color="white">mdi-eye-outline</v-icon>
                  <span>View album</span>
                </div>
              </div>
              <div class="photo-count-badge">
                <v-icon size="12" color="white">mdi-image-multiple</v-icon>
                <span>{{ item.documents ? item.documents.length : 0 }}</span>
              </div>
            </div>

            <!-- Meta -->
            <div class="album-meta">
              <div class="album-meta-top">
                <div class="album-title" :title="item.title">
                  {{ item.title }}
                </div>
                <div class="album-date">
                  <v-icon size="11" color="var(--tx3)"
                    >mdi-clock-outline</v-icon
                  >
                  {{ item.created_at | formatDate }}
                </div>
              </div>
              <div class="album-actions">
                <button
                  class="action-btn edit"
                  @click="$router.push('/societyadmin/gallery/edit/' + item.id)"
                  title="Edit album"
                >
                  <v-icon size="18" color="var(--brand)">mdi-pencil-outline</v-icon>
                </button>
                <button
                  class="action-btn delete"
                  @click="confirmDelete(item.id)"
                  title="Delete album"
                >
                  <v-icon size="18" color="#EF4444">mdi-trash-can-outline</v-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirm -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Album"
      message="Are you sure you want to delete this album? All photos within it will be permanently removed."
      confirm-text="Yes, delete permanently"
      cancel-text="Keep album"
      :loading="deleteLoading"
      @confirm="deleteGallery"
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import ConfirmModal from "../../common/ConfirmModal.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "GalleryList",
  mixins: [filterMixin],
  components: { ConfirmModal },
  data() {
    return {
      search: "",
      loading: false,
      deleteDialog: false,
      deleteLoading: false,
      galleries: [],
      delete_id: "",
    };
  },
  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,
    totalPhotos() {
      return this.galleries.reduce(
        (sum, g) => sum + (g.documents ? g.documents.length : 0),
        0,
      );
    },
  },
  watch: {
    search() {
      this.getGalleries();
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    getGalleries() {
      this.loading = true;
      this.$axios
        .get("societyadmin/gallery", {
          params: { search: this.search || "" },
        })
        .then((response) => {
          this.loading = false;
          if (response.data.status) {
            this.galleries = response.data.galleries;
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    deleteGallery() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/gallery/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          if (response.data.status) {
            this.deleteDialog = false;
            this.getGalleries();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: 2000,
              message: response.data.message,
            });
          } else {
            this.showError(response.data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },
    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY") : "N/A";
    },
    onSearch() {
      // Logic handled by mixin watcher
    },
    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },
    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: 2000,
        message: msg,
      });
    },
  },
  mounted() {
    // Initialize persistent filters for this module
    this.initFilters("gallery");

    this.getGalleries();
  },
};
</script>

<style scoped>
.view-page-container {
  padding: 24px;
}

/* ── Unified Card ── */
.udc {
  background: var(--surface);
  border-radius: 20px;
  border: 1px solid var(--border);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Header ── */
.udc-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}
.udc-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.udc-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.udc-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(13, 153, 114, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.udc-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--tx);
  margin-bottom: 5px;
  line-height: 1.2;
}
.udc-meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.udc-meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.meta-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.meta-dot.green {
  background: #0d9972;
}
.meta-dot.purple {
  background: #534ab7;
}
.udc-meta-text {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tx3);
}
.udc-meta-sep {
  color: var(--border);
  font-size: 14px;
}

/* Right side controls */
.udc-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 38px;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 10px;
  min-width: 200px;
  transition: border-color 0.2s;
}
.search-wrap:focus-within {
  border-color: var(--brand);
}
.search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: var(--tx);
  width: 100%;
}
.search-input::placeholder {
  color: var(--tx3);
}

.udc-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: var(--btn-color, #0d9972);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;
}
.udc-add-btn:hover {
  opacity: 0.88;
}

/* ── Body ── */
.udc-body {
  background: var(--surface2);
  padding: 16px;
  min-height: 300px;
}

/* Loading / Empty */
.state-center {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 10px;
  text-align: center;
}
.empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--surface);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--tx);
}
.empty-sub {
  font-size: 13px;
  color: var(--tx3);
  max-width: 340px;
  line-height: 1.6;
}

/* ── Gallery Grid ── */
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

/* ── Album Card ── */
.album-card {
  /* background: var(--surface); */
  border-radius: 16px;
  border: 1px solid var(--border);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}
.album-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.07);
  border-color: var(--brand);
}

/* Cover */
.album-cover {
  position: relative;
  aspect-ratio: 4/3;
  background: var(--surface2);
  cursor: pointer;
  overflow: hidden;
}
.cover-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.album-card:hover .cover-img {
  transform: scale(1.06);
}

/* Placeholder when no cover image */
.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(135deg, var(--surface) 0%, var(--surface2) 100%);
}
.placeholder-label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--tx3);
  letter-spacing: 0.02em;
}

.cover-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 20, 35, 0.38);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s;
}
.album-cover:hover .cover-overlay {
  opacity: 1;
}

.view-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 30px;
  background: var(--btn-color, #0d9972);
  color: white;
  font-size: 12px;
  font-weight: 700;
  transform: translateY(6px);
  transition: transform 0.25s;
}
.album-cover:hover .view-btn {
  transform: translateY(0);
}

.photo-count-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(10, 20, 35, 0.6);
  color: white;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
  backdrop-filter: blur(4px);
}

/* Meta */
.album-meta {
  padding: 14px 14px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.album-meta-top {
  flex: 1;
  min-width: 0;
}
.album-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 3px;
}
.album-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
}

.album-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  /* border: 1px solid var(--border); */
  /* background: var(--surface2); */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
}
.action-btn.edit:hover {
  border-color: var(--brand);
  background: rgba(13, 153, 114, 0.06);
}
.action-btn.edit:hover .v-icon {
  color: var(--brand) !important;
}
.action-btn.delete:hover {
  border-color: rgba(239, 68, 68, 0.3);
  background: #fef2f2;
}
.action-btn.delete:hover .v-icon {
  color: #ef4444 !important;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .udc-top {
    flex-direction: column;
    align-items: flex-start;
  }
  .udc-right {
    width: 100%;
  }
  .search-wrap {
    flex: 1;
    min-width: 0;
  }
  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}
@media (max-width: 480px) {
  .gallery-grid {
    grid-template-columns: 1fr;
  }
}
</style>
